export const DEFAULT_DOCUMENT_TYPE = "RUT";

export const normalizeRut = (value: string) =>
  value.replace(/[^\dk]/gi, "").toLowerCase();

export function rutCheckDigit(body: string): string {
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += Number(body.charAt(i)) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const check = 11 - (sum % 11);
  return check === 11 ? "0" : check === 10 ? "k" : String(check);
}

// Takes a normalized RUT; an 8-digit run like 11111111 is a placeholder, not a person.
export function isValidRut(normalized: string): boolean {
  const body = normalized.slice(0, -1);
  return (
    /^\d{7,8}$/.test(body) &&
    !/(.)\1{7}/.test(body) &&
    rutCheckDigit(body) === normalized.slice(-1).toLowerCase()
  );
}

export function cleanDocumentNumber(
  value: string,
  documentType: string = DEFAULT_DOCUMENT_TYPE
): string {
  if (documentType !== DEFAULT_DOCUMENT_TYPE) return value.trim();

  const cleaned = normalizeRut(value);
  const trimmed = value.trim();
  // normalizeRut strips every char outside [0-9kK]; a non-RUT-shaped input
  // (e.g. a deliberately wrong document number made of letters) cleans to
  // "", which must never leave the frontend as document_number. The raw
  // trimmed value is strictly better than "" in that case.
  return cleaned === "" && trimmed !== "" ? trimmed : cleaned;
}

export function formatDocumentNumber(
  value: string,
  documentType: string = DEFAULT_DOCUMENT_TYPE
): string {
  if (documentType !== DEFAULT_DOCUMENT_TYPE) return value.trim();

  const clean = cleanDocumentNumber(value, documentType);
  if (clean.length < 2) return clean;
  // cleanDocumentNumber falls back to the trimmed raw input when RUT-cleaning
  // would empty it (e.g. "WRONGDOC") — that raw value isn't RUT-shaped, so
  // don't dash/dot-format it.
  if (/[^0-9k]/i.test(clean)) return value.trim();

  const body = clean.slice(0, -1);
  const checkDigit = clean.slice(-1).toUpperCase();
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}-${checkDigit}`;
}
